import { useEffect, useState } from "react";
import React from "react";
import { DashboardLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, GraduationCap, Ticket, Copy, Download, Eye, Building2, Calendar, Mail, Phone, User, FileText, Upload, ImageIcon, ShieldCheck, ShieldAlert, Activity, AlertTriangle, RefreshCw, ExternalLink, Send, Filter, FileDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { exportGraduatesCSV, exportGraduatesPDF } from "@/lib/exportGraduates";
import { SkeletonTable } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Certificate from "@/components/Certificate";

const emptyCert = { graduate_name: "", degree: "", major: "", graduation_date: "", gpa: "", honors: "" };

export default function InstitutionDashboard() {
  const { user } = useAuth();
  const [institution, setInstitution] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [validations, setValidations] = useState([]);
  const [validationsLoaded, setValidationsLoaded] = useState(false);
  const [insights, setInsights] = useState(null);
  const [tab, setTab] = useState("certificates");
  const [yearFilter, setYearFilter] = useState("all");

  // certificate dialog
  const [certOpen, setCertOpen] = useState(false);
  const [certForm, setCertForm] = useState(emptyCert);
  const [savingCert, setSavingCert] = useState(false);

  // voucher dialog
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCount, setVoucherCount] = useState(20);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [justGenerated, setJustGenerated] = useState(null);

  // bulk import
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // branding
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [uploadingBranding, setUploadingBranding] = useState(false);

  // preview certificate
  const [previewCert, setPreviewCert] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    if (!user?.institution_id) return;
    try {
      // Institution admins can no longer see vouchers (L2 concern) — omit the call
      const [iRes, cRes, insRes] = await Promise.all([
        api.get(`/institutions/${user.institution_id}`),
        api.get("/certificates"),
        api.get(`/institutions/${user.institution_id}/insights`),
      ]);
      setInstitution(iRes.data);
      setCertificates(cRes.data);
      setVouchers([]);
      setInsights(insRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadValidations = async () => {
    if (!user?.institution_id) return;
    try {
      const { data } = await api.get(`/institutions/${user.institution_id}/validations?limit=200`);
      setValidations(data);
      setValidationsLoaded(true);
    } catch {
      toast.error("Failed to load validation log");
    }
  };

  // Refetch when the signed-in institution changes. `reload` isn't memoized;
  // it captures fresh state on each render so we intentionally omit it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [user?.institution_id]);

  // Lazy-load validations only when the audit-log tab is first opened.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "validations" && !validationsLoaded) {
      loadValidations();
    }
  }, [tab, validationsLoaded]);

  const handleCreateCert = async (e) => {
    e.preventDefault();
    setSavingCert(true);
    try {
      const { data } = await api.post("/certificates", certForm);
      toast.success(`Certificate issued · ${data.certificate_id}`);
      setCertForm(emptyCert);
      setCertOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingCert(false);
    }
  };

  const handleGenerateVouchers = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const payload = { count: Number(voucherCount) };
      if (recipientEmail.trim()) payload.recipient_email = recipientEmail.trim();
      const { data } = await api.post("/vouchers/generate", payload);
      setJustGenerated(data);
      toast.success(
        recipientEmail.trim()
          ? `${data.length} vouchers generated · email queued to ${recipientEmail.trim()}`
          : `${data.length} voucher codes generated`
      );
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/certificates/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.count} certificates imported${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleSendDigest = async () => {
    if (!user?.institution_id) return;
    try {
      await api.post(`/institutions/${user.institution_id}/send-digest`);
      toast.success("Digest email queued. Check your inbox.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send digest");
    }
  };

  const handleUploadBranding = async (e) => {
    e.preventDefault();
    if (!user?.institution_id) return;
    setUploadingBranding(true);
    try {
      if (logoFile) {
        const fd = new FormData();
        fd.append("kind", "logo");
        fd.append("file", logoFile);
        await api.post(`/institutions/${user.institution_id}/assets`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      if (templateFile) {
        const fd = new FormData();
        fd.append("kind", "template");
        fd.append("file", templateFile);
        await api.post(`/institutions/${user.institution_id}/assets`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Branding updated");
      setBrandingOpen(false);
      setLogoFile(null);
      setTemplateFile(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingBranding(false);
    }
  };

  const copyVouchers = () => {
    if (!justGenerated) return;
    const text = justGenerated.map((v) => v.code).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Voucher codes copied to clipboard");
  };

  const downloadVouchers = () => {
    if (!justGenerated) return;
    const rows = [
      ["Voucher Code", "Expires On", "Uses Allowed"],
      ...justGenerated.map((v) => [
        v.code,
        v.expires_at ? new Date(v.expires_at).toLocaleDateString() : "—",
        String(v.uses_remaining ?? 3),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `certiseal-vouchers-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const usedVouchers = vouchers.filter((v) => v.used_count > 0).length;
  const availableVouchers = vouchers.filter((v) => !v.bound_certificate_id && v.uses_remaining > 0).length;

  // Unique graduation years derived from certificates
  const uniqueYears = React.useMemo(() => {
    const set = new Set();
    certificates.forEach((c) => {
      const y = String(c.graduation_date || "").slice(0, 4);
      if (/^\d{4}$/.test(y)) set.add(y);
    });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [certificates]);

  const filteredCerts = React.useMemo(() => {
    if (yearFilter === "all") return certificates;
    return certificates.filter((c) => String(c.graduation_date || "").slice(0, 4) === yearFilter);
  }, [certificates, yearFilter]);

  return (
    <DashboardLayout title={institution?.name || "Institution"} subtitle="Issue credentials and manage your registry.">
      {/* Institution profile bento */}
      {loading && !institution && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="inst-profile-skeleton">
          <Card className="p-6 col-span-1 md:col-span-2 border-slate-200 dark:border-slate-800">
            <div className="flex items-start gap-4">
              <Skeleton className="w-12 h-12 rounded-md flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-3">
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-3 w-24" />
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Skeleton className="w-4 h-4 rounded" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  ))}
                </div>
                <Skeleton className="h-3 w-3/4" />
              </div>
            </div>
          </Card>
          <Card className="p-6 border-slate-200 dark:border-slate-800">
            <Skeleton className="h-3 w-32 mb-3" />
            <Skeleton className="h-14 w-24 my-2" />
            <Skeleton className="h-4 w-28 mt-3" />
            <Skeleton className="h-10 w-full mt-4" />
          </Card>
        </div>
      )}
      {institution && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="inst-profile-grid">
          <Card className="p-6 col-span-1 md:col-span-2 border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-blue-600" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-display font-bold text-lg text-slate-900">{institution.name}</h3>
                    <p className="text-sm text-slate-500 mt-0.5">Est. {institution.year_established}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`/institutions/${institution.slug}`} target="_blank" rel="noreferrer" data-testid="view-public-profile-link">
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-1.5" /> Public profile
                      </Button>
                    </a>
                    <Button variant="outline" size="sm" onClick={handleSendDigest} data-testid="send-digest-btn">
                      <Send className="w-4 h-4 mr-1.5" /> Send digest
                    </Button>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <InfoLine icon={User} label="President" value={institution.president} />
                  <InfoLine icon={Mail} label="Email" value={institution.email} />
                  <InfoLine icon={Phone} label="Contact" value={institution.contact} />
                  <InfoLine icon={Calendar} label="Established" value={institution.year_established} />
                </div>
                <div className="mt-4 text-sm text-slate-600 flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                  <span data-testid="inst-address">{institution.address}</span>
                </div>
              </div>
            </div>
          </Card>
          <Card className="p-6 border-slate-200 flex flex-col justify-between" data-testid="inst-stats-card">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Total graduates issued</div>
              {institution.logo_path && (
                <img src={fileUrl(institution.logo_path)} alt="" className="w-10 h-10 object-contain rounded border border-slate-200 bg-white p-0.5" data-testid="inst-logo-preview" />
              )}
            </div>
            <div className="mt-2 font-display font-extrabold text-6xl text-slate-900 tracking-tight">{institution.graduate_count}</div>
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
              <Badge variant="secondary" className="font-mono text-[10px]">{certificates.length} certificates</Badge>
            </div>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => setBrandingOpen(true)} data-testid="branding-btn">
              <ImageIcon className="w-4 h-4 mr-2" /> Branding & template
            </Button>
          </Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        {insights && insights.suspicious_certs && insights.suspicious_certs.length > 0 && (
          <Card className="mb-6 p-5 border-red-200 bg-red-50/60" data-testid="fraud-banner">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-md bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-base text-red-900">Suspicious verification activity</h3>
                  <span className="text-xs text-red-700">last 24 hours</span>
                </div>
                <p className="text-sm text-red-800 mt-1">
                  {insights.suspicious_certs.length} credential{insights.suspicious_certs.length > 1 ? "s have" : " has"} unusual failed-verification patterns. Review the audit log for details.
                </p>
                <ul className="mt-3 space-y-1.5" data-testid="fraud-suspicious-list">
                  {insights.suspicious_certs.slice(0, 5).map((s) => (
                    <li key={s.certificate_id} className="text-xs text-red-900 flex items-center gap-3 flex-wrap">
                      <span className="font-mono tracking-wider bg-white/70 border border-red-200 px-1.5 py-0.5 rounded">{s.certificate_id}</span>
                      {s.graduate_name && <span className="text-slate-700">{s.graduate_name}</span>}
                      <span className="text-red-700">{s.failed_count} failed · {s.unique_ips} IP{s.unique_ips === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Button variant="outline" size="sm" onClick={() => setTab("validations")} className="border-red-200 hover:bg-red-100 text-red-900" data-testid="fraud-review-btn">
                Review log
              </Button>
            </div>
          </Card>
        )}

        <TabsList className="bg-slate-100" data-testid="dashboard-tabs">
          <TabsTrigger value="certificates" data-testid="tab-certificates">Certificates ({certificates.length})</TabsTrigger>
          <TabsTrigger value="validations" data-testid="tab-validations">
            Validations{insights?.total_validations ? ` (${insights.total_validations})` : ""}
          </TabsTrigger>
        </TabsList>

        {/* Certificates */}
        <TabsContent value="certificates" className="mt-6">
          <Card className="border-slate-200">
            <div className="p-6 flex items-center justify-between border-b border-slate-200">
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900">Issued certificates</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {filteredCerts.length} {filteredCerts.length === 1 ? "credential" : "credentials"}
                  {yearFilter !== "all" ? ` from ${yearFilter}` : " issued by your institution."}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <Select value={yearFilter} onValueChange={setYearFilter}>
                    <SelectTrigger className="w-[140px] h-9" data-testid="year-filter-select"><SelectValue placeholder="All years" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All years</SelectItem>
                      {uniqueYears.map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" disabled={filteredCerts.length === 0} data-testid="export-graduates-btn">
                      <FileDown className="w-4 h-4 mr-2" /> Download
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => exportGraduatesCSV(filteredCerts, institution)} data-testid="export-csv-item">
                      CSV — spreadsheet
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportGraduatesPDF(filteredCerts, institution)} data-testid="export-pdf-item">
                      PDF — printable
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="bulk-import-btn">
                  <Upload className="w-4 h-4 mr-2" /> Bulk import
                </Button>
                <Dialog open={certOpen} onOpenChange={setCertOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 font-display font-semibold" data-testid="add-certificate-btn">
                    <Plus className="w-4 h-4 mr-2" /> Issue certificate
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto dialog-scroll-hint">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Issue new certificate</DialogTitle>
                    <DialogDescription>Fill in graduate details. A live preview is shown on the right.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateCert} className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-2">
                    <div className="space-y-4">
                      <FieldInput label="Graduate full name" required value={certForm.graduate_name} onChange={(v) => setCertForm({ ...certForm, graduate_name: v })} testId="cert-name-input" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FieldInput label="Degree" required placeholder="Bachelor of Science" value={certForm.degree} onChange={(v) => setCertForm({ ...certForm, degree: v })} testId="cert-degree-input" />
                        <FieldInput label="Major / Field (optional)" placeholder="Computer Science" value={certForm.major} onChange={(v) => setCertForm({ ...certForm, major: v })} testId="cert-major-input" />
                      </div>
                      <FieldInput type="date" label="Graduation date" required value={certForm.graduation_date} onChange={(v) => setCertForm({ ...certForm, graduation_date: v })} testId="cert-date-input" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FieldInput label="GPA (optional)" value={certForm.gpa} onChange={(v) => setCertForm({ ...certForm, gpa: v })} testId="cert-gpa-input" />
                        <FieldInput label="Honors (optional)" placeholder="Summa Cum Laude" value={certForm.honors} onChange={(v) => setCertForm({ ...certForm, honors: v })} testId="cert-honors-input" />
                      </div>
                      <DialogFooter className="pt-2">
                        <Button type="button" variant="ghost" onClick={() => setCertOpen(false)}>Cancel</Button>
                        <Button type="submit" disabled={savingCert} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-certificate-btn">
                          {savingCert ? "Issuing…" : "Issue certificate"}
                        </Button>
                      </DialogFooter>
                    </div>
                    <div className="lg:sticky lg:top-4 self-start">
                      <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Live preview</div>
                      <div className="scale-[0.82] origin-top-left lg:scale-90 transform-gpu">
                        <Certificate
                          certificate={{
                            certificate_id: "CS-PREVIEW00",
                            graduate_name: certForm.graduate_name || "Graduate Name",
                            degree: certForm.degree || "Degree Title",
                            major: certForm.major,
                            graduation_date: certForm.graduation_date || new Date().toISOString(),
                            honors: certForm.honors,
                          }}
                          institution={institution || { name: "Institution", year_established: "—", address: "—", president: "—" }}
                        />
                      </div>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Certificate ID</TableHead>
                  <TableHead>Graduate</TableHead>
                  <TableHead>Degree</TableHead>
                  <TableHead>Graduation</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="certificates-table-body">
                {loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="p-0">
                      <SkeletonTable rows={4} cols={4} testId="certs-skeleton" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredCerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      {certificates.length === 0 ? "No certificates yet. Issue your first one above." : "No certificates match the selected filter."}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredCerts.map((c) => (
                  <TableRow key={c.id} data-testid={`cert-row-${c.id}`}>
                    <TableCell className="font-mono text-xs tracking-wider text-slate-700">{c.certificate_id}</TableCell>
                    <TableCell className="font-medium text-slate-900">{c.graduate_name}</TableCell>
                    <TableCell className="text-slate-700">{c.degree}{c.major ? (<><span className="text-slate-400"> · </span><span className="text-slate-500">{c.major}</span></>) : null}</TableCell>
                    <TableCell className="text-slate-600">{new Date(c.graduation_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => setPreviewCert(c)} data-testid={`view-cert-${c.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Validations audit log */}
        <TabsContent value="validations" className="mt-6">
          <Card className="border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" /> Verification audit log
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">Every attempt to validate a credential — successful and failed.</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex gap-3 text-xs">
                    <div className="flex items-center gap-1.5 text-emerald-700">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" /> {validations.filter(v => v.success).length} successful
                    </div>
                    <div className="flex items-center gap-1.5 text-red-700">
                      <span className="w-2 h-2 rounded-full bg-red-500" /> {validations.filter(v => !v.success).length} failed
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={loadValidations} data-testid="refresh-validations-btn">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
                  </Button>
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Certificate</TableHead>
                  <TableHead>Voucher</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Source IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="validations-table-body">
                {!validationsLoaded && (
                  <TableRow>
                    <TableCell colSpan={5} className="p-0">
                      <SkeletonTable rows={4} cols={5} testId="validations-skeleton" />
                    </TableCell>
                  </TableRow>
                )}
                {validationsLoaded && validations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      No verification attempts yet.
                    </TableCell>
                  </TableRow>
                )}
                {validations.map((v) => (
                  <TableRow key={v.id} data-testid={`validation-row-${v.id}`}>
                    <TableCell className="text-slate-600 text-sm">
                      {new Date(v.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-xs text-slate-800">{v.certificate_id || <span className="text-slate-400">—</span>}</div>
                      {v.graduate_name && <div className="text-xs text-slate-500 truncate max-w-xs">{v.graduate_name}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-700">{v.voucher_code}</TableCell>
                    <TableCell>
                      {v.success ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                          <ShieldCheck className="w-3 h-3 mr-1" /> Verified
                        </Badge>
                      ) : (
                        <div>
                          <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200">
                            <ShieldAlert className="w-3 h-3 mr-1" /> Failed
                          </Badge>
                          {v.error && <div className="text-[10px] text-slate-500 mt-1 max-w-xs truncate">{v.error}</div>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{v.ip || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview dialog */}
      <Dialog open={!!previewCert} onOpenChange={(o) => !o && setPreviewCert(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto dialog-scroll-hint">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> Certificate preview
            </DialogTitle>
            <DialogDescription>Preview of the issued certificate as rendered for verifiers.</DialogDescription>
          </DialogHeader>
          {previewCert && institution && <Certificate certificate={previewCert} institution={institution} />}
        </DialogContent>
      </Dialog>

      {/* Bulk CSV import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" /> Bulk import graduates
            </DialogTitle>
            <DialogDescription>
              Upload a CSV with columns: <span className="font-mono text-xs">Graduate Name, Degree, Graduation Date</span> (optional: Major, GPA, Honors).
            </DialogDescription>
          </DialogHeader>
          {!importResult ? (
            <form onSubmit={handleBulkImport} className="space-y-5 pt-2">
              <label className="flex items-start gap-3 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors">
                <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Upload className="w-4 h-4 text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  {importFile ? (
                    <>
                      <div className="text-sm font-medium text-slate-900 truncate">{importFile.name}</div>
                      <div className="text-xs text-slate-500">{(importFile.size / 1024).toFixed(1)} KB · click to replace</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-slate-700">Choose a CSV file</div>
                      <div className="text-xs text-slate-500">UTF-8 encoded, first row is header</div>
                    </>
                  )}
                </div>
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
              </label>
              <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent("Graduate Name,Degree,Graduation Date,Major,GPA,Honors\nJane Doe,Bachelor of Science,2024-06-15,Computer Science,3.9,Summa Cum Laude\nJohn Smith,Master of Arts,2024-05-20,History,,\n")}`}
                download="certiseal-graduates-template.csv"
                className="text-xs text-blue-600 hover:underline"
                data-testid="download-csv-template"
              >
                Download CSV template
              </a>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setImportOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={importing || !importFile} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-import-btn">
                  {importing ? "Importing…" : "Import certificates"}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="pt-2 space-y-4" data-testid="import-result">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="text-sm text-emerald-800">
                  <strong>{importResult.count}</strong> certificates issued.
                  {importResult.errors.length > 0 && <span className="text-amber-700 ml-2"><strong>{importResult.errors.length}</strong> rows skipped.</span>}
                </div>
              </div>
              {importResult.errors.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-48 overflow-y-auto text-xs">
                  {importResult.errors.map((err, i) => (
                    <div key={i} className="text-slate-600 py-0.5">Row {err.row}: {err.error}</div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => { setImportOpen(false); setImportResult(null); setImportFile(null); }} className="bg-blue-600 hover:bg-blue-700">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Branding dialog */}
      <Dialog open={brandingOpen} onOpenChange={setBrandingOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-blue-600" /> Branding & template
            </DialogTitle>
            <DialogDescription>Upload your institution logo and certificate design. New certificates will use these assets.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadBranding} className="space-y-4 pt-2">
            <BrandFileDrop label="Logo" icon={ImageIcon} hint="PNG/JPEG/SVG · shown on cards + certificate header" accept="image/*" file={logoFile} onFile={setLogoFile} current={institution?.logo_path} testId="branding-logo-input" />
            <BrandFileDrop label="Certificate template" icon={FileText} hint="PNG/JPEG image recommended (used as background). PDF stored for reference." accept="image/*,application/pdf" file={templateFile} onFile={setTemplateFile} current={institution?.cert_template_path} testId="branding-template-input" />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setBrandingOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={uploadingBranding || (!logoFile && !templateFile)} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-branding-btn">
                {uploadingBranding ? "Uploading…" : "Save branding"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function BrandFileDrop({ label, icon: Icon, hint, accept, file, onFile, current, testId }) {
  return (
    <div>
      <Label className="text-sm font-medium text-slate-600 mb-1.5 block">{label}</Label>
      <label className="flex items-start gap-3 p-3 border border-dashed border-slate-300 rounded-md cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors">
        <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-slate-600" />
        </div>
        <div className="min-w-0 flex-1">
          {file ? (
            <>
              <div className="text-sm font-medium text-slate-900 truncate">{file.name}</div>
              <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB · click to replace</div>
            </>
          ) : current ? (
            <>
              <div className="text-sm font-medium text-slate-700">Replace current file</div>
              <div className="text-xs text-slate-500 truncate">{current.split("/").pop()}</div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-700">Click to upload</div>
              <div className="text-xs text-slate-500">{hint}</div>
            </>
          )}
        </div>
        <input type="file" className="hidden" accept={accept} onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid={testId} />
      </label>
    </div>
  );
}

function InfoLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
        <div className="text-sm text-slate-700 truncate">{value}</div>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, required, type = "text", placeholder, min, max, testId }) {
  return (
    <div>
      <Label className="text-sm font-medium text-slate-600 mb-1.5 block">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} min={min} max={max} className="h-11 border-slate-200" data-testid={testId} />
    </div>
  );
}
